import React, { Component } from "react";
import Modal from "@cloudscape-design/components/modal";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Alert from "@cloudscape-design/components/alert";
import Tabs from "@cloudscape-design/components/tabs";
import Spinner from "@cloudscape-design/components/spinner";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { configStore } from "../stores/config";
import { userStore } from "../stores/access";
import { gamesSweepstakesModalStore } from "../stores/gamesSweepstakesModal";
import GamesSweepstakesGameTab from "./GamesSweepstakesGameTab";
import GamesSweepstakesTriggersTab from "./GamesSweepstakesTriggersTab";

class GamesSweepstakesModal extends Component {
	gameTabRef = React.createRef();

	triggersTabRef = React.createRef();

	setModalError = (text, code = 500) => {
		this.props.setGamesSweepstakesModal((prev) => ({
			...prev,
			loaded: true,
			error: {
				text,
				code,
				timestamp: Date.now(),
			},
		}));
	};

	canViewTriggersTab = () => this.props.user.level >= 6 || this.props.user.showColumn.includes("sweepstakesTriggers");

	loadSweepstakes = async () => {
		const { setGamesSweepstakesModal, Interface, gamesSweepstakesModal } = this.props;
		const canViewTriggersTab = this.canViewTriggersTab();

		setGamesSweepstakesModal((prev) => ({
			...prev,
			loaded: false,
			error: { text: "", code: 0, timestamp: 0 },
		}));

		try {
			const [gameResponse, triggerResponse] = await Promise.all([Interface.getSweepstakes(), canViewTriggersTab ? Interface.request("get", { class: "data", module: "customGame", method: "getSweepstakesEmotionalTriggers" }, {}) : Promise.resolve(null)]);
			const values = GamesSweepstakesGameTab.mapSweepstakesToFormValues(gameResponse, gamesSweepstakesModal.currentOption);
			const triggerState = canViewTriggersTab
				? GamesSweepstakesTriggersTab.buildTriggerStateFromResponse(triggerResponse || {})
				: {
						triggerValues: GamesSweepstakesTriggersTab.createInitialTriggerValues(),
						triggerEditor: GamesSweepstakesTriggersTab.createInitialTriggerEditorState(),
				  };

			setGamesSweepstakesModal((prev) => ({
				...prev,
				show: true,
				loaded: true,
				activeTab: canViewTriggersTab && prev.activeTab === "triggers" ? "triggers" : "game",
				values,
				triggerValues: triggerState.triggerValues,
				triggerEditor: triggerState.triggerEditor,
				error: { text: "", code: 0, timestamp: 0 },
			}));
		} catch (error) {
			setGamesSweepstakesModal((prev) => ({
				...prev,
				show: true,
				loaded: true,
				triggerValues: GamesSweepstakesTriggersTab.createInitialTriggerValues(),
				triggerEditor: GamesSweepstakesTriggersTab.createInitialTriggerEditorState(),
				error: {
					text: error.text || error.toString(),
					code: error.code || 500,
					timestamp: error.timestamp || Date.now(),
				},
			}));
			Interface.addNoty("Error", error.text || error.toString());
		}
	};

	closeModal = () => {
		this.props.setGamesSweepstakesModal((prev) => ({
			...prev,
			show: false,
			activeTab: "game",
			triggerEditor: {
				...(prev.triggerEditor || GamesSweepstakesTriggersTab.createInitialTriggerEditorState()),
				activeScenarioTab: "lessThanTicket",
			},
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		}));
	};

	setActiveTab = (activeTab) => {
		this.props.setGamesSweepstakesModal((prev) => ({
			...prev,
			activeTab,
		}));
	};

	handleSave = () => {
		if (this.canViewTriggersTab() && this.props.gamesSweepstakesModal.activeTab === "triggers") {
			this.triggersTabRef.current?.save();
			return;
		}

		this.gameTabRef.current?.save();
	};

	getSaveButtonLabel = () => {
		return this.canViewTriggersTab() && this.props.gamesSweepstakesModal.activeTab === "triggers" ? "Save triggers" : "Save game settings";
	};

	componentDidMount() {
		this.loadSweepstakes();
	}

	render() {
		const { gamesSweepstakesModal, config, setGamesSweepstakesModal, Interface } = this.props;
		const canViewTriggersTab = this.canViewTriggersTab();
		const tabs = [
			{
				id: "game",
				label: "Game settings",
				content: <GamesSweepstakesGameTab ref={this.gameTabRef} config={config} Interface={Interface} gamesSweepstakesModal={gamesSweepstakesModal} setGamesSweepstakesModal={setGamesSweepstakesModal} />,
			},
			{
				id: "triggers",
				label: "Sweepstakes triggers",
				content: <GamesSweepstakesTriggersTab ref={this.triggersTabRef} Interface={Interface} gamesSweepstakesModal={gamesSweepstakesModal} setGamesSweepstakesModal={setGamesSweepstakesModal} />,
			},
		].filter((tab) => (tab.id === "triggers" ? canViewTriggersTab : true));
		const activeTabId = canViewTriggersTab && gamesSweepstakesModal.activeTab === "triggers" ? "triggers" : "game";

		return (
			<Modal
				visible={gamesSweepstakesModal.show}
				onDismiss={this.closeModal}
				header="Sweepstakes Custom Game Management"
				size="large"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button variant="link" onClick={this.closeModal}>
								Cancel
							</Button>
							<Button variant="primary" onClick={this.handleSave} loading={!gamesSweepstakesModal.loaded} disabled={!gamesSweepstakesModal.loaded}>
								{this.getSaveButtonLabel()}
							</Button>
						</SpaceBetween>
					</Box>
				}>
				{gamesSweepstakesModal.loaded ? (
					<SpaceBetween size="m">
						{gamesSweepstakesModal.error.text ? (
							<Alert type="error">
								{gamesSweepstakesModal.error.text}
								{gamesSweepstakesModal.error.code ? ` | code: ${gamesSweepstakesModal.error.code}` : ""}
							</Alert>
						) : null}
						{tabs.length === 1 ? tabs[0].content : <Tabs tabs={tabs} activeTabId={activeTabId} onChange={({ detail }) => this.setActiveTab(detail.activeTabId)} />}
					</SpaceBetween>
				) : (
					<Box textAlign="center">
						<Spinner size="large" />
					</Box>
				)}
			</Modal>
		);
	}
}

function withRecoil(WrappedComponent) {
	return function RecoilComponent(props) {
		const gamesSweepstakesModal = useRecoilValue(gamesSweepstakesModalStore);
		const setGamesSweepstakesModal = useSetRecoilState(gamesSweepstakesModalStore);
		const config = useRecoilValue(configStore);
		const user = useRecoilValue(userStore);

		return <WrappedComponent {...props} user={user} config={config} gamesSweepstakesModal={gamesSweepstakesModal} setGamesSweepstakesModal={setGamesSweepstakesModal} />;
	};
}

export default withRecoil(GamesSweepstakesModal);
